// src/components/ui/CampaignWizard.tsx
import React, { useState } from "react";
import { Check, ChevronRight, Target, Users, Radio, DollarSign, BarChart2, Rocket, X, Tv, Smartphone, Globe, Zap } from "lucide-react";
import { clsx } from "clsx";
import { AUDIENCES } from "@/data";
import { identityGraphStats } from "@/data/identityGraph";

const STEPS = [
  { id: 1, label: "Objective",  icon: Target     },
  { id: 2, label: "Audience",   icon: Users      },
  { id: 3, label: "Channels",   icon: Radio      },
  { id: 4, label: "Budget",     icon: DollarSign },
  { id: 5, label: "Review",     icon: BarChart2  },
];

const OBJECTIVES = [
  { value: "awareness",     label: "Awareness",     desc: "Maximize reach and brand visibility"   },
  { value: "consideration", label: "Consideration", desc: "Drive research and product engagement" },
  { value: "conversion",    label: "Conversion",    desc: "Generate purchases and sign-ups"       },
  { value: "retention",     label: "Retention",     desc: "Re-engage existing customers"          },
];

const CHANNELS = [
  {
    value:       "ctv",
    label:       "Connected TV",
    desc:        "Person-level CTV targeting — biggest screen in the home",
    reach:       "17.4B",
    reachSub:    "linked CTV IFAs",
    recommended: true,
    icon:        Tv,
    color:       "bg-navy/10 text-navy",
    badge:       "Person-Level",
  },
  {
    value:       "display",
    label:       "Open Web Display",
    desc:        "Premium programmatic, direct DSP — 30% lower CPM",
    reach:       "498M",
    reachSub:    "addressable individuals",
    recommended: true,
    icon:        Globe,
    color:       "bg-blue/10 text-blue",
    badge:       "30% Lower Cost",
  },
  {
    value:       "oo",
    label:       "McClatchy O&O",
    desc:        "30+ owned properties, 90M+ monthly uniques, high trust",
    reach:       "90M+",
    reachSub:    "monthly uniques",
    recommended: true,
    icon:        Zap,
    color:       "bg-cyan/10 text-cyan",
    badge:       "Exclusive",
  },
  {
    value:       "mobile",
    label:       "Mobile / In-App",
    desc:        "MAID-based targeting — cross-device with person identity",
    reach:       "5.88B",
    reachSub:    "mobile device IDs",
    recommended: false,
    icon:        Smartphone,
    color:       "bg-blue/10 text-blue",
    badge:       null,
  },
  {
    value:       "video",
    label:       "Online Video",
    desc:        "Pre/mid-roll across premium inventory",
    reach:       "38M",
    reachSub:    "video reach",
    recommended: false,
    icon:        Radio,
    color:       "bg-slate/10 text-slate",
    badge:       null,
  },
];

// ── Default sequence ──────────────────────────────────────────
const DEFAULT_SEQUENCE = [
  { step: 1, channel: "CTV",     label: "Awareness",    color: "bg-navy",  desc: "Build brand awareness on the big screen" },
  { step: 2, channel: "Display", label: "Retargeting",  color: "bg-blue",  desc: "Re-engage across open web" },
  { step: 3, channel: "O&O",     label: "Conversion",   color: "bg-cyan",  desc: "Drive conversion on owned properties" },
  { step: 4, channel: "Mobile",  label: "Reminder",     color: "bg-blue",  desc: "Final nudge on mobile devices" },
];

function fmt(n: number): string {
  if (n >= 1_000_000_000) return `${(n/1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000)     return `${(n/1_000_000).toFixed(1)}M`;
  if (n >= 1_000)         return `${(n/1_000).toFixed(0)}K`;
  return n.toLocaleString();
}

interface WizardProps { onClose: () => void; }

export default function CampaignWizard({ onClose }: WizardProps) {
  const [step, setStep]           = useState(1);
  const [objective, setObjective] = useState("");
  const [audience, setAudience]   = useState("");
  const [channels, setChannels]   = useState<string[]>(["ctv","display","oo"]);
  const [campaignName, setCampaignName] = useState("");
  const [budget, setBudget]       = useState("250000");
  const [holdout, setHoldout]     = useState("10");
  const [launched, setLaunched]   = useState(false);

  const toggleChannel = (ch: string) =>
    setChannels(prev => prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]);

  const canNext = () => {
    if (step === 1) return !!objective && !!campaignName;
    if (step === 2) return !!audience;
    if (step === 3) return channels.length > 0;
    if (step === 4) return !!budget && Number(budget) > 0;
    return true;
  };

  const sel = AUDIENCES.find(a => a.id === audience);

  // Cross-device reach estimate
  const audienceSize  = sel?.estimatedSize ?? 0;
  const ctvReach      = Math.round(audienceSize * (identityGraphStats.ctv.linkedToIdentity / identityGraphStats.individuals) * 1.4);
  const mobileReach   = Math.round(audienceSize * (identityGraphStats.mobileDevices.total  / identityGraphStats.individuals) * 1.18);

  if (launched) return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl p-10 max-w-sm w-full text-center animate-fade-in">
        <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
          <Rocket size={28} className="text-emerald-500" />
        </div>
        <h2 className="text-navy mb-2">Campaign Launched!</h2>
        <p className="text-sm text-slate mb-1 font-semibold">{campaignName}</p>
        <p className="text-xs text-slate mb-6">Live across {channels.length} channel{channels.length > 1 ? "s" : ""}. Attribution tracking active.</p>
        <div className="grid grid-cols-2 gap-3 mb-6 text-left">
          {[
            { label: "Budget",      value: "$" + (Number(budget)/1000).toFixed(0) + "K"           },
            { label: "Audience",    value: sel ? fmt(sel.estimatedSize) : "--"                     },
            { label: "CTV Reach",   value: audienceSize > 0 ? fmt(ctvReach) + " screens" : "--"   },
            { label: "Mobile Reach",value: audienceSize > 0 ? fmt(mobileReach) + " devices" : "--"},
          ].map((item, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-3">
              <p className="text-2xs text-slate uppercase tracking-wide">{item.label}</p>
              <p className="text-sm font-semibold text-navy tabular">{item.value}</p>
            </div>
          ))}
        </div>
        <button onClick={onClose} className="btn-primary w-full">View Campaign</button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-fade-in">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-navy">New Campaign</h3>
          <button onClick={onClose} className="btn-ghost p-1.5 rounded-md"><X size={16} /></button>
        </div>

        {/* Step indicators */}
        <div className="flex items-center px-6 py-4 border-b border-gray-100 gap-1">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <button onClick={() => s.id < step && setStep(s.id)}
                className={clsx("flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all",
                  step === s.id ? "bg-blue text-white" :
                  s.id < step  ? "bg-emerald-50 text-emerald-700 cursor-pointer hover:bg-emerald-100" :
                  "text-slate cursor-default")}>
                {s.id < step ? <Check size={11} /> : <s.icon size={11} />}
                {s.label}
              </button>
              {i < STEPS.length - 1 && <ChevronRight size={13} className="text-gray-200 flex-shrink-0" />}
            </React.Fragment>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* Step 1 — Objective */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="input-label">Campaign Name</label>
                <input className="input" placeholder="e.g. DeWalt Q3 Power Tools — CTV Launch" value={campaignName} onChange={e => setCampaignName(e.target.value)} />
              </div>
              <div>
                <label className="input-label mb-2 block">Campaign Objective</label>
                <div className="grid grid-cols-2 gap-3">
                  {OBJECTIVES.map(obj => (
                    <button key={obj.value} onClick={() => setObjective(obj.value)}
                      className={clsx("text-left p-3 rounded-lg border-2 transition-all duration-150",
                        objective === obj.value ? "border-blue bg-blue/5" : "border-gray-200 hover:border-gray-300")}>
                      <p className={clsx("text-sm font-semibold", objective === obj.value ? "text-blue" : "text-gray-800")}>{obj.label}</p>
                      <p className="text-xs text-slate mt-0.5">{obj.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2 — Audience */}
          {step === 2 && (
            <div className="space-y-3">
              <p className="text-sm text-slate mb-2">Select a saved audience segment.</p>
              {AUDIENCES.filter(a => a.status === "active").map(seg => (
                <button key={seg.id} onClick={() => setAudience(seg.id)}
                  className={clsx("w-full text-left p-3 rounded-lg border-2 transition-all duration-150",
                    audience === seg.id ? "border-blue bg-blue/5" : "border-gray-200 hover:border-gray-300")}>
                  <div className="flex items-center justify-between">
                    <p className={clsx("text-sm font-semibold", audience === seg.id ? "text-blue" : "text-gray-800")}>{seg.name}</p>
                    <span className="text-xs font-semibold text-navy tabular">
                      {seg.estimatedSize >= 1_000_000 ? fmt(seg.estimatedSize) : (seg.estimatedSize/1_000).toFixed(0)+"K"}
                    </span>
                  </div>
                  <p className="text-xs text-slate mt-0.5">{seg.filters[0]}</p>
                </button>
              ))}
            </div>
          )}

          {/* Step 3 — Channels */}
          {step === 3 && (
            <div className="space-y-3">
              <p className="text-sm text-slate mb-1">Select activation channels. Person-level frequency caps apply across all.</p>

              {CHANNELS.map(ch => (
                <button key={ch.value} onClick={() => toggleChannel(ch.value)}
                  className={clsx("w-full text-left p-3 rounded-lg border-2 transition-all duration-150",
                    channels.includes(ch.value) ? "border-blue bg-blue/5" : "border-gray-200 hover:border-gray-300")}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={clsx("w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0",
                        channels.includes(ch.value) ? "border-blue bg-blue" : "border-gray-300")}>
                        {channels.includes(ch.value) && <Check size={10} className="text-white" />}
                      </div>
                      <div className={clsx("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", ch.color)}>
                        <ch.icon size={15} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className={clsx("text-sm font-semibold", channels.includes(ch.value) ? "text-blue" : "text-gray-800")}>{ch.label}</p>
                          {ch.recommended && <span className="badge badge-cyan text-2xs">Recommended</span>}
                          {ch.badge && !ch.recommended && <span className="badge badge-navy text-2xs">{ch.badge}</span>}
                        </div>
                        <p className="text-xs text-slate">{ch.desc}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs font-bold text-navy tabular">{ch.reach}</p>
                      <p className="text-2xs text-slate">{ch.reachSub}</p>
                    </div>
                  </div>
                </button>
              ))}

              {/* Cross-device reach summary */}
              {sel && (
                <div className="bg-navy/5 border border-navy/10 rounded-lg p-3 mt-2">
                  <p className="text-xs font-semibold text-navy mb-2">Cross-Device Reach for This Audience</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: "Unique Persons", value: fmt(sel.estimatedSize),  icon: Users,       color: "text-navy"  },
                      { label: "CTV Screens",    value: fmt(ctvReach),           icon: Tv,          color: "text-navy"  },
                      { label: "Mobile Devices", value: fmt(mobileReach),        icon: Smartphone,  color: "text-blue"  },
                    ].map((m, i) => (
                      <div key={i} className="bg-white rounded-lg p-2 text-center border border-navy/10">
                        <m.icon size={13} className={clsx("mx-auto mb-1", m.color)} />
                        <p className={clsx("text-sm font-bold tabular", m.color)}>{m.value}</p>
                        <p className="text-2xs text-slate">{m.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sequencing canvas */}
              <div className="border border-gray-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-navy mb-2">Recommended Sequencing</p>
                <div className="flex items-center gap-1">
                  {DEFAULT_SEQUENCE.map((s, i) => (
                    <React.Fragment key={i}>
                      <div className={clsx("flex-1 rounded-lg p-2 text-center", s.color + "/10")}>
                        <p className={clsx("text-2xs font-bold", s.color.replace("bg-","text-"))}>{s.channel}</p>
                        <p className="text-2xs text-slate">{s.label}</p>
                      </div>
                      {i < DEFAULT_SEQUENCE.length - 1 && <ChevronRight size={12} className="text-gray-300 flex-shrink-0" />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 4 — Budget */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="input-label">Total Budget ($)</label><input className="input" type="number" value={budget} onChange={e => setBudget(e.target.value)} /></div>
                <div><label className="input-label">Daily Cap ($)</label><input className="input" type="number" defaultValue={Math.round(Number(budget)/30)} /></div>
                <div><label className="input-label">Start Date</label><input className="input" type="date" defaultValue="2026-04-01" /></div>
                <div><label className="input-label">End Date</label><input className="input" type="date" defaultValue="2026-06-30" /></div>
                <div><label className="input-label">Frequency Cap (per person/week)</label><input className="input" type="number" defaultValue={5} /></div>
                <div><label className="input-label">Holdout % (incrementality)</label><input className="input" type="number" value={holdout} onChange={e => setHoldout(e.target.value)} /></div>
              </div>
              <div><label className="input-label">Attribution Model</label>
                <select className="input"><option>Data-driven (recommended)</option><option>Last touch</option><option>Linear</option><option>Time decay</option></select>
              </div>
            </div>
          )}

          {/* Step 5 — Review */}
          {step === 5 && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                {[
                  { label: "Campaign Name", value: campaignName },
                  { label: "Objective",     value: objective.charAt(0).toUpperCase() + objective.slice(1) },
                  { label: "Audience",      value: sel?.name ?? "--" },
                  { label: "Est. Reach",    value: sel ? fmt(sel.estimatedSize) + " persons" : "--" },
                  { label: "Channels",      value: channels.join(", ").toUpperCase() },
                  { label: "Budget",        value: "$" + (Number(budget)/1000).toFixed(0) + "K total" },
                  { label: "Holdout",       value: holdout + "% for incrementality testing" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start justify-between gap-4">
                    <span className="text-xs text-slate w-32 flex-shrink-0">{item.label}</span>
                    <span className="text-xs font-medium text-gray-800 text-right">{item.value}</span>
                  </div>
                ))}
              </div>

              {/* Cross-device summary */}
              {sel && (
                <div className="bg-navy/5 border border-navy/10 rounded-lg p-3">
                  <p className="text-xs font-semibold text-navy mb-2">Cross-Device Reach Summary</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: "Unique Persons", value: fmt(sel.estimatedSize), icon: Users,      color: "text-navy" },
                      { label: "CTV Screens",    value: fmt(ctvReach),          icon: Tv,         color: "text-navy" },
                      { label: "Mobile Devices", value: fmt(mobileReach),       icon: Smartphone, color: "text-blue" },
                    ].map((m, i) => (
                      <div key={i} className="bg-white rounded-lg p-2 text-center border border-navy/10">
                        <m.icon size={12} className={clsx("mx-auto mb-0.5", m.color)} />
                        <p className={clsx("text-sm font-bold tabular", m.color)}>{m.value}</p>
                        <p className="text-2xs text-slate">{m.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-emerald-700 mb-2">Estimated Performance</p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: "Est. Impressions", value: Math.round(Number(budget)/0.022/1_000_000).toFixed(1) + "M" },
                    { label: "Est. Conversions", value: Math.round(Number(budget)*0.018).toLocaleString() },
                    { label: "Target ROAS",      value: "5.5-7.2x" },
                  ].map((m, i) => (
                    <div key={i} className="text-center">
                      <p className="text-sm font-bold text-emerald-700 tabular">{m.value}</p>
                      <p className="text-2xs text-emerald-600">{m.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
          <button onClick={() => step > 1 ? setStep(s => s - 1) : onClose()} className="btn-secondary btn-sm">
            {step === 1 ? "Cancel" : "Back"}
          </button>
          <div className="flex items-center gap-1.5">
            {STEPS.map(s => (
              <div key={s.id} className={clsx("rounded-full transition-all duration-200",
                step === s.id ? "w-4 h-1.5 bg-blue" :
                s.id < step  ? "w-1.5 h-1.5 bg-emerald-400" :
                "w-1.5 h-1.5 bg-gray-200")} />
            ))}
          </div>
          {step < 5
            ? <button onClick={() => setStep(s => s + 1)} disabled={!canNext()} className="btn-primary btn-sm">Next</button>
            : <button onClick={() => setLaunched(true)} className="btn-accent btn-sm flex items-center gap-1.5"><Rocket size={13} /> Launch</button>
          }
        </div>
      </div>
    </div>
  );
}